import { Controller } from "@hotwired/stimulus"

/**
 * Testimonial Carousel Controller
 * A unified carousel that works responsively across all screen sizes
 */
export default class extends Controller {
  static values = { identifier: String }

  static targets = ["slide", "container"]
  
  connect() {
    // Core configuration
    this.currentSlide = 0
    this.slidesPerView = window.innerWidth < 1000 ? 1 : 2 // Reduced slides per view for larger slides
    
    // Calculate slide width based on viewport for mobile
    this.calculateSlideWidth()
    this.slideMargin = window.innerWidth < 1000 ? 6 : 10 // Margin between slides (mx-3 or mx-5)
    
    // Calculate total slides and pages
    this.totalSlides = this.slideTargets.length
    this.maxSlide = Math.max(0, this.totalSlides - this.slidesPerView)
    
    // Touch/drag handling state
    this.isDragging = false
    this.startX = 0
    this.currentTranslate = 0
    this.prevTranslate = 0
    this.dragThreshold = 50
    
    // Initialize event listeners and view
    this.setupEventListeners()
    
    // Initialize with a slight delay to ensure DOM is ready
    setTimeout(() => {
      this.updateCarousel()
    }, 100)
  }
  
  disconnect() {
    this.removeEventListeners()
  }
  
  // Event management
  setupEventListeners() {
    // Store bound references to event handlers for easier removal
    this.resizeHandler = this.handleResize.bind(this)
    this.touchStartHandler = this.touchStart.bind(this)
    this.touchMoveHandler = this.touchMove.bind(this)
    this.touchEndHandler = this.touchEnd.bind(this)
    
    // Add event listeners
    window.addEventListener('resize', this.resizeHandler)
    
    // Touch events (mobile)
    this.containerTarget.addEventListener('touchstart', this.touchStartHandler, { passive: true })
    this.containerTarget.addEventListener('touchmove', this.touchMoveHandler, { passive: false })
    this.containerTarget.addEventListener('touchend', this.touchEndHandler, { passive: true })
    
    // Mouse events (desktop)
    this.containerTarget.addEventListener('mousedown', this.touchStartHandler)
    window.addEventListener('mousemove', this.touchMoveHandler)
    window.addEventListener('mouseup', this.touchEndHandler)
  }
  
  removeEventListeners() {
    // Remove all event listeners
    window.removeEventListener('resize', this.resizeHandler)
    
    this.containerTarget.removeEventListener('touchstart', this.touchStartHandler)
    this.containerTarget.removeEventListener('touchmove', this.touchMoveHandler)
    this.containerTarget.removeEventListener('touchend', this.touchEndHandler)
    
    this.containerTarget.removeEventListener('mousedown', this.touchStartHandler)
    window.removeEventListener('mousemove', this.touchMoveHandler)
    window.removeEventListener('mouseup', this.touchEndHandler)
  }
  
  // Calculate the slide width based on device width for mobile
  calculateSlideWidth() {
    if (window.innerWidth < 1000) {
      // On mobile: 90% of viewport width to match the CSS
      this.slideWidth = window.innerWidth * 0.9
    } else {
      // On desktop: fixed width
      this.slideWidth = 300
    }
  }
  
  // Respond to viewport changes
  handleResize() {
    // Update slidesPerView based on screen width
    this.slidesPerView = window.innerWidth < 1000 ? 1 : 2
    
    // Recalculate slide width for responsive sizing
    this.calculateSlideWidth()
    
    this.slideMargin = window.innerWidth < 1000 ? 6 : 10
    this.maxSlide = Math.max(0, this.totalSlides - this.slidesPerView)
    
    // Ensure current slide is valid after resize
    this.currentSlide = Math.min(this.currentSlide, this.maxSlide)
    
    // Update carousel with new settings
    this.updateCarousel()
  }
  
  // Navigation methods
  next() {
    if (this.currentSlide < this.maxSlide) {
      this.currentSlide++
      this.updateCarousel(true)
    }
  }
  
  prev() {
    if (this.currentSlide > 0) {
      this.currentSlide--
      this.updateCarousel(true)
    }
  }
  
  // Core carousel positioning logic
  updateCarousel(animated = false) {
    // Apply transition based on context
    this.containerTarget.style.transition = this.isDragging ? 'none' : 
      (animated ? 'transform 300ms ease-out' : 'none')
    
    // Calculate position based on device type
    const isMobile = window.innerWidth < 1000
    const slideUnit = this.slideWidth + (this.slideMargin * 2)
    
    // Calculate the position to translate to
    let translateX
    
    if (isMobile) {
      // Mobile: Center the current slide in viewport
      const viewportCenter = (window.innerWidth - this.slideWidth) / 2
      translateX = -1 * this.currentSlide * slideUnit + viewportCenter - this.slideMargin
    } else {
      // Desktop: Add padding to prevent edge touching
      const containerPadding = 16
      translateX = -1 * this.currentSlide * slideUnit + containerPadding
    }
    
    // Update stored position for dragging reference
    this.currentTranslate = translateX
    this.prevTranslate = translateX
    
    // Apply the transform
    this.containerTarget.style.transform = `translateX(${translateX}px)`
    
    // Update slides visibility
    this.updateSlidesVisibility()
  }
  
  // Touch/drag handling
  touchStart(e) {
    this.isDragging = true
    this.startX = this.getPositionX(e)
    
    // Get current transform value
    const style = window.getComputedStyle(this.containerTarget)
    const transform = style.getPropertyValue('transform')
    const matrix = new DOMMatrixReadOnly(transform)
    this.prevTranslate = matrix.m41
    
    // Remove transition for responsive dragging
    this.containerTarget.style.transition = 'none'
  }
  
  touchMove(e) {
    if (!this.isDragging) return
    
    const currentPosition = this.getPositionX(e)
    const diff = currentPosition - this.startX
    
    // Prevent page scrolling while dragging carousel
    if (Math.abs(diff) > 5) {
      e.preventDefault()
    }
    
    // Calculate new position
    let newTranslate = this.prevTranslate + diff
    
    // Add resistance when trying to scroll past boundaries
    if (this.currentSlide === 0 && diff > 0) {
      // Apply resistance when trying to scroll left of first slide
      newTranslate = this.prevTranslate + (diff * 0.3) // Reduced movement
    } else if (this.currentSlide === this.maxSlide && diff < 0) {
      // Apply resistance when trying to scroll right of last slide
      newTranslate = this.prevTranslate + (diff * 0.3) // Reduced movement
    }
    
    // Apply drag movement with boundaries
    this.currentTranslate = newTranslate
    this.containerTarget.style.transform = `translateX(${this.currentTranslate}px)`
  }
  
  touchEnd(e) {
    if (!this.isDragging) return
    this.isDragging = false
    
    // Calculate drag distance
    const currentPosition = this.getPositionX(e)
    const diff = currentPosition - this.startX
    
    // Re-enable transition for smooth snapping
    this.containerTarget.style.transition = 'transform 300ms ease'
    
    // Determine if we should change slide, enforcing boundaries
    if (Math.abs(diff) > this.dragThreshold) {
      if (diff > 0 && this.currentSlide > 0) {
        // Prev slide - but don't go before first slide
        this.currentSlide--
      } else if (diff < 0 && this.currentSlide < this.maxSlide) {
        // Next slide - but don't go after last slide
        this.currentSlide++
      }
    }
    
    // Enforce boundaries with bounce animation when trying to scroll past limits
    if ((this.currentSlide === 0 && diff > 0) || (this.currentSlide === this.maxSlide && diff < 0)) {
      // Animate back to boundary with a slight bounce effect
      this.containerTarget.style.transition = 'transform 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)'
    }
    
    // Update carousel position to proper slide
    this.updateCarousel(true)
  }
  
  // Helper to get position from either mouse or touch event
  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX
  }
  
  // Simple slide visibility update - all slides with the same style
  updateSlidesVisibility() {
    const visibleRange = 5 // Increase the range to make more slides interactive
    
    this.slideTargets.forEach((slide, index) => {
      const distance = Math.abs(index - this.currentSlide)
      
      // All slides have the same opacity
      slide.style.opacity = '1'
      slide.style.transform = 'scale(1)'
      
      // Always keep slides visible but only make nearby ones interactive
      slide.style.visibility = 'visible'
      slide.style.pointerEvents = distance < visibleRange ? 'auto' : 'none'
    })
  }
}
